<template>
  <div class="chart">
    <Bar :options="chartOptions" :data="mapData" />
  </div>
</template>

<script lang="ts" setup>
import { Bar } from 'vue-chartjs';
import { ChartConfig } from './chartConfig';
import { Data, DataFilter, LabelsAndData } from '../../models';
import { chartDataUtil } from '@/features/reports/util/ChartDataUtil';
import ColourService from '@/shared/services/color';
import WordWrapUtil from '@/shared/util/wordWrapUtil';
import { computed, PropType } from 'vue';
import type { ChartData, ChartOptions } from 'chart.js';
import merge from 'lodash.merge';

// Since Chart.js components are registered globally in main.ts via 'chart.js/auto',
// we don't need to register them here.

const props = defineProps({
  data: {
    type: Object as PropType<Data>,
    required: true,
  },
  dataFilter: {
    type: Object as PropType<DataFilter>,
    required: false,
  },
  barColour: {
    type: String,
    required: true,
  },
  thresholdData: {
    type: Number,
    required: true,
  },
  thresholdLineColour: {
    type: String,
    required: true,
  },
  orderAsc: {
    type: Boolean,
    default: false,
  },
});

const chartOptions = computed(() => {
  const overrides = {
    indexAxis: 'y', // Makes the bar chart horizontal
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      inlineLabels: false,
      crosshair: false,
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            const tooltipItem = tooltipItems[0];
            const lineLength = 24;
            let name = tooltipItem.label as string;
            name = WordWrapUtil.breakWordsOverLimit(name, lineLength);
            return WordWrapUtil.wrap(name, lineLength);
          },
          label: (tooltipItem) => {
            const score = parseFloat(tooltipItem.raw as string).toFixed(3);
          },
        },
      },
      annotation: {
        annotations: {
          thresholdLine: {
            type: 'line',
            xMin: lineMarkerPosition.value,
            xMax: lineMarkerPosition.value,
            borderColor: props.thresholdLineColour,
            borderDash: [8],
            borderWidth: 2,
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: false, text: props.data.scale.title },
        ticks: {
          min: 0, // Replaced beginAtZero
          max: 1,
          maxTicksLimit: 20,
        },
      },
      y: {
        type: 'category',
        ticks: {
          callback: (value: number, index: number, ticks: any[]) => {
            // value is the index of the label, not the label itself
            const label = orderedData.value.labels[value as number];
            const truncated = label ? label.substring(0, 15) : '';
            return truncated.length === label.length ? truncated : `${truncated.trim()}...`;
          },
        },
        grid: {
          display: false,
        },
        afterFit: (scale) => {
          const labelData = scale.ticks.map((t) => ({
            label: t.label,
            width: scale.ctx.measureText(t.label).width,
          }));

          const maxLabelWidth = Math.max(...labelData.map((data) => data.width));
          scale.width = maxLabelWidth + 10;
        },
      },
    },
  };

  return merge({}, ChartConfig.gridChartsGlobalOptions, overrides);
});

const orderedData = computed<LabelsAndData>(() =>
  chartDataUtil.orderData(
    { data: props.data.series[0].data, labels: props.data.scale.labels },
    {
      dataOrderAscending: props.orderAsc,
      forceZeroValuesToBottom: true,
    },
  ),
);

const mapData = computed<ChartData<'bar'>>(() => {
  const series = props.data.series[0];

  return {
    labels: orderedData.value.labels,
    datasets: [
      {
        label: series.displayName,
        data: orderedData.value.data,
        backgroundColor: props.barColour,
        borderColor: props.barColour,
        hoverBackgroundColor: ColourService.setAlpha(props.barColour, 0.4),
        maxBarThickness: 32,
      },
    ],
  };
});

const lineMarkerPosition = computed(() => props.thresholdData);
</script>

<style lang="scss" scoped>
.chart {
  height: 250px;
  max-height: 250px;
  position: relative;
  canvas {
    position: relative;
    z-index: 10;
  }
}
</style>
